import { ChangeDetectionStrategy, Component, Input, ViewChild, ElementRef, AfterViewInit, ChangeDetectorRef, EventEmitter, Output, inject } from '@angular/core';
import { MediaImage, ImageData, MediaVideo, Image, IMediaItem, WindowRefService, ICustomWindow, ButtonComponent, ImageItemComponent } from '@sirius/common';

@Component({
    selector: 'commerce-product-image-thumbnails',
    templateUrl: './product-image-thumbnails.component.html',
    styleUrls: ['./product-image-thumbnails.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [ButtonComponent, ImageItemComponent]
})
export class ProductImageThumbnailsComponent implements AfterViewInit {
    private cdr = inject(ChangeDetectorRef);
    private _windowRef = inject(WindowRefService);

    @Input() mediaItems: IMediaItem[] = [];
    @Input() imageData?: ImageData;
    @Output() thumbnailClick = new EventEmitter<number>();

    // Add Input setter to watch for changes to currentIndex and ensure thumbnail visibility
    @Input() set currentIndex(value: number) {
        this._currentIndex = value;
        // Only call ensureThumbnailVisible if component is initialized
        if (this.thumbnailsContainer) {
            this.ensureThumbnailVisible(value);
        }
    }
    get currentIndex(): number {
        return this._currentIndex;
    }

    private get Window(): ICustomWindow | undefined {
        return this._windowRef.NativeWindow;
    }

    private _currentIndex = 0;

    @ViewChild('thumbnailsContainer') thumbnailsContainer?: ElementRef;

    thumbnailsPerPage = 5;
    currentThumbnailPage = 0;


    ngAfterViewInit(): void {
        this.updateThumbnailVisibility();
    }

    getThumbnailMediaItem(item: IMediaItem): MediaImage | null {
        if (item.type === 'video') {
            const videoItem = item as MediaVideo;
            if (videoItem.thumbnail) {
                const thumbnailImage = new Image(videoItem.thumbnail.url, videoItem.thumbnail.source);
                return new MediaImage(thumbnailImage, videoItem.title);
            }
            return null;
        }
        return item as MediaImage;
    }

    previousThumbnails(): void {
        if (this.currentThumbnailPage > 0) {
            this.currentThumbnailPage--;
            this.scrollThumbnails();
        }
    }

    nextThumbnails(): void {
        const maxPage = Math.ceil(this.mediaItems.length / this.thumbnailsPerPage) - 1;
        if (this.currentThumbnailPage < maxPage) {
            this.currentThumbnailPage++;
            this.scrollThumbnails();
        }
    }

    private scrollThumbnails(): void {
        if (!this.Window) {
            return;
        }

        if (!this.thumbnailsContainer) return;

        const container = this.thumbnailsContainer.nativeElement;
        const scrollAmount = container.offsetWidth;
        container.scrollTo({
            left: this.currentThumbnailPage * scrollAmount,
            behavior: 'smooth',
        });
    }

    private updateThumbnailVisibility(): void {
        if (!this.thumbnailsContainer) return;

        const container = this.thumbnailsContainer.nativeElement;
        const thumbnailWidth = 92;
        const gap = 8;
        const visibleWidth = container.offsetWidth;
        this.thumbnailsPerPage = Math.floor(visibleWidth / (thumbnailWidth + gap));
        this.ensureThumbnailVisible(this.currentIndex);
        this.cdr.detectChanges();
    }

    private ensureThumbnailVisible(index: number): void {
        if (!this.thumbnailsContainer) return;

        const targetPage = Math.floor(index / this.thumbnailsPerPage);
        if (targetPage !== this.currentThumbnailPage) {
            this.currentThumbnailPage = targetPage;
            this.scrollThumbnails();
        }
    }

    get showPrevThumbnailButton(): boolean {
        return this.currentThumbnailPage > 0;
    }

    get showNextThumbnailButton(): boolean {
        const maxPage = Math.ceil(this.mediaItems.length / this.thumbnailsPerPage) - 1;
        return this.currentThumbnailPage < maxPage;
    }
}
